/**
 * AI Quota External Store
 *
 * A lightweight external store that tracks per-bucket AI usage quotas
 * (questions, imports, insights, question_assist). API responses include
 * quota headers which are parsed and fed into this store via
 * `updateBucketQuota`.
 *
 * ## Pattern: External Store with `subscribe` / `getSnapshot`
 *
 * This store follows the "external store" contract expected by React 18's
 * `useSyncExternalStore`:
 *   - `getAiQuotaSnapshot()` — returns an immutable snapshot of the state
 *   - `subscribe(listener)` — registers a callback; returns an unsubscribe fn
 *
 * Because the app currently targets **React 17**, components consume this
 * store with a `useState` + `useEffect` subscription pattern instead:
 *
 * ```ts
 * const [quota, setQuota] = useState(getAiQuotaSnapshot);
 * useEffect(() => subscribe(() => setQuota(getAiQuotaSnapshot())), []);
 * ```
 *
 * Note: this pattern has a small window where updates can be missed between
 * mount and subscription (the "tearing" problem). The React 18 migration to
 * `useSyncExternalStore` eliminates this gap as a correctness fix, not just
 * syntactic sugar.
 *
 * When the app upgrades to React 18+ the consumption can be simplified to:
 *
 * ```ts
 * const quota = useSyncExternalStore(subscribe, getAiQuotaSnapshot);
 * ```
 *
 * No changes to the store itself will be required — the API is already
 * compatible.
 *
 * @see https://react.dev/reference/react/useSyncExternalStore
 * @see https://github.com/reactwg/react-18/discussions/86
 */

/** The supported AI feature buckets that have independent weekly quotas. */
export type AiQuotaBucket =
  | 'questions'
  | 'imports'
  | 'insights'
  | 'question_assist';

/** Quota state for a single bucket as returned by the API. */
export type BucketQuota = {
  bucket: AiQuotaBucket;
  weeklyLimit: number;
  weeklyRemaining: number;
  weeklyResetAt: string;
};

/**
 * Aggregate quota state keyed by bucket name.
 * Partial because not every bucket may have been reported yet.
 */
export type AiQuotaState = Partial<Record<AiQuotaBucket, BucketQuota>>;

// --- localStorage persistence ------------------------------------------------
//
// Why not Jotai `atomWithStorage`?
// The axios interceptor writes quota data outside the React tree, which would
// require a global Jotai store instance. Keeping this as a standalone external
// store with direct localStorage is simpler and preserves `useSyncExternalStore`
// compatibility for the React 18 migration.
//
// Storage key pattern: socrative_ai_quota:${publicId}
// Each user's quota is stored under their own key, preventing cross-account
// stale data issues. The current user's publicId is tracked in module state
// so the axios interceptor can write to the correct key.

/** @internal Exported for tests only. */
export const STORAGE_KEY_PREFIX = 'socrative_ai_quota';

function getStorageKey(publicId: string): string {
  return `${STORAGE_KEY_PREFIX}:${publicId}`;
}

function loadFromStorage(publicId: string): AiQuotaState {
  try {
    const key = getStorageKey(publicId);
    const raw = localStorage.getItem(key);
    if (!raw) return {};

    const parsed: AiQuotaState = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    const now = new Date();
    const filtered: AiQuotaState = {};

    for (const [bucketKey, bucket] of Object.entries(parsed)) {
      if (bucket && new Date(bucket.weeklyResetAt) > now) {
        filtered[bucketKey as AiQuotaBucket] = bucket;
      }
    }

    return filtered;
  } catch (error) {
    console.warn(
      '[aiQuota] Failed to load quota state from localStorage',
      error
    );
    return {};
  }
}

function saveToStorage(publicId: string, s: AiQuotaState): void {
  try {
    const key = getStorageKey(publicId);
    localStorage.setItem(key, JSON.stringify(s));
  } catch (error) {
    // Private browsing or storage full — log for observability.
    console.warn(
      '[aiQuota] Failed to persist quota state to localStorage',
      error
    );
  }
}

function clearStorage(publicId: string): void {
  try {
    const key = getStorageKey(publicId);
    localStorage.removeItem(key);
  } catch {
    // Ignore — storage unavailable.
  }
}

// --- Store internals --------------------------------------------------------

/**
 * The publicId of the currently authenticated user. Set by setCurrentUser()
 * when the user logs in or the account data is fetched. The axios interceptor
 * and other callers use this to write quota to the correct storage key.
 */
let currentPublicId: string | null = null;

let state: AiQuotaState = {};
const listeners = new Set<() => void>();

// --- Public API (external store contract) -----------------------------------

/**
 * Return the current immutable state snapshot.
 * Serves as the `getSnapshot` argument for `useSyncExternalStore`.
 */
export function getAiQuotaSnapshot(): AiQuotaState {
  return state;
}

/**
 * Set the currently authenticated user's publicId. This should be called
 * when the user logs in or when account data is fetched. The publicId is
 * used to determine which localStorage key to read/write quota data to.
 *
 * When a new publicId is set, the store loads that user's quota from storage.
 */
export function setCurrentUser(publicId: string): void {
  if (currentPublicId === publicId) {
    return;
  }
  currentPublicId = publicId;
  state = loadFromStorage(publicId);
  listeners.forEach((l) => l());
}

/**
 * Upsert a single bucket's quota data for the currently authenticated user.
 * Creates a new state reference (immutable update) so that React's
 * referential equality checks (`Object.is`) detect the change, then notifies
 * all subscribers.
 *
 * Requires setCurrentUser() to have been called first to establish which
 * user's quota to update.
 */
export function updateBucketQuota(quota: BucketQuota): void {
  if (!currentPublicId) {
    // This can happen on initial page load when axios interceptor fires before
    // useAccount completes. Silently skip in production; log in dev for debugging.
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[aiQuota] updateBucketQuota called before setCurrentUser — quota not persisted'
      );
    }
    return;
  }
  if (
    !Number.isFinite(quota.weeklyLimit) ||
    !Number.isFinite(quota.weeklyRemaining) ||
    quota.weeklyLimit < 0 ||
    quota.weeklyRemaining < 0
  ) {
    return;
  }
  state = { ...state, [quota.bucket]: quota };
  saveToStorage(currentPublicId, state);
  listeners.forEach((l) => l());
}

/**
 * Clear all quota state for the current user — call this on logout to
 * remove the user's quota from localStorage and reset in-memory state.
 */
export function clearAiQuota(): void {
  if (currentPublicId) {
    clearStorage(currentPublicId);
  }
  currentPublicId = null;
  state = {};
  listeners.forEach((l) => l());
}

/**
 * Register a listener that is called whenever the state changes.
 * Returns an unsubscribe function — matches the `subscribe` signature
 * expected by `useSyncExternalStore`.
 */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Get the current user's publicId. Only for use in tests.
 * @internal
 */
export function _getCurrentPublicId(): string | null {
  return currentPublicId;
}

/** Reset store to empty state. Only for use in tests. */
export function _resetForTesting(): void {
  currentPublicId = 'test-user';
  state = {};
  listeners.clear();
  try {
    // Clear all quota keys (pattern: socrative_ai_quota:*)
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore — may not be available in all test environments.
  }
}
